// Space-train data: the consist, car lengths, and the paint table that turns the authored
// meshes' material names into colour + shader material codes.
// The meshes themselves are authored in Blender: web/public/assets/train/_src/*.py
// (`python3 build_train.py` regenerates train.glb + train_atlas.png).

export const DET_NONE = 0, DET_PLATE = 64, DET_LETTER = 128, DET_LOUVRE = 192;

export interface Paint {
  /** sRGB hex */
  c: number;
  /** emissive 0..1 */
  e?: number;
  /** metalness 0..1 (packed to 4 bits) */
  m?: number;
  /** roughness 0..1 (packed to 4 bits) */
  r?: number;
  /** patina 0..1 */
  p?: number;
  /** detail kind byte */
  d?: number;
}

// Weathered steel-blue plating, copper and brass trim, one cream livery band (refs + art guide §9).
export const PAINTS: Record<string, Paint> = {
  plate: { c: 0x778c86, m: 0.25, r: 0.45, p: 1, d: DET_PLATE },
  plateD: { c: 0x5f6f6a, m: 0.2, r: 0.52, p: 1, d: DET_PLATE },
  cop: { c: 0x93625a, m: 0.6, r: 0.42, p: 0.7, d: DET_PLATE },
  copD: { c: 0x5c3d33, m: 0.6, r: 0.55, p: 0.6, d: DET_PLATE },
  copRoof: { c: 0x86584e, m: 0.6, r: 0.48, p: 0.85, d: DET_PLATE },
  brass: { c: 0x9c865e, m: 1, r: 0.14, p: 0.3 },
  brassD: { c: 0x76664c, m: 1, r: 0.38, p: 0.4 },
  cream: { c: 0xe8dec4, m: 0, r: 0.6, p: 0.15, d: DET_LETTER },
  under: { c: 0x2a2723, m: 0.4, r: 0.75, p: 0.2 },
  iron: { c: 0x3a3a36, m: 0.7, r: 0.5, p: 0.3, d: DET_LOUVRE },
  ironP: { c: 0x3a3a36, m: 0.7, r: 0.55, p: 0.3 },
  win: { c: 0xffc870, e: 1, d: DET_LOUVRE },
  winPlain: { c: 0xffc870, e: 1, d: DET_LOUVRE },
  winCold: { c: 0xffb868, e: 0.8, d: DET_LOUVRE },
  // gauge dials and lamp globes: cream enamel, NOT lit glass (no yellow on non-glass parts)
  winGauge: { c: 0xd6ccb4, m: 0, r: 0.35 },
  furnace: { c: 0xff8a3d, e: 1 },
  thr: { c: 0xffe6b0, e: 1 },
  glass: { c: 0x8fa6a8, m: 0, r: 0.1, p: 0 },
  // the caboose's oxide red (ref B's rear cars), weathered like everything else
  red: { c: 0x8a4a3c, m: 0.35, r: 0.55, p: 0.55, d: DET_PLATE },
};

export type CarType = 'loco' | 'tender' | 'reactor' | 'exec' | 'saloon' | 'security' | 'steerage' | 'freight' | 'tail' | 'dorm' | 'post';

/** Whole-train scale applied at load: a ~32 m car pitch, the loco ~87 m: a hulking presence over the arena. */
export const TRAIN_SCALE = 1.9;
/** Authored coupling-to-coupling lengths (the loco is authored at 1.25× its 40 m base). */
const BASE_LEN: Record<CarType, number> = {
  loco: 50, tender: 17, reactor: 17, exec: 17, saloon: 17, security: 17, steerage: 17, freight: 17, tail: 18, dorm: 17, post: 17,
};
export const CAR_LEN = Object.fromEntries(Object.entries(BASE_LEN).map(([k, v]) => [k, v * TRAIN_SCALE])) as Record<CarType, number>;

/** Nose → tail, 26 vehicles (Godot DIRECTION.md consist, steampunk types). */
export const CONSIST: CarType[] = [
  // first class up front, then steerage broken up by service cars so the rake never reads as
  // a run of identical capsules
  // a run of identical capsules; no two neighbours share a type (or a silhouette)
  // every third car is a tall double-deck dorm with oversized domed pods: a skyline, not a loaf
  'loco', 'tender', 'reactor', 'dorm', 'exec', 'saloon', 'dorm', 'security', 'post',
  'dorm', 'steerage', 'reactor', 'dorm', 'steerage', 'freight', 'dorm', 'exec', 'steerage',
  'dorm', 'post', 'reactor', 'dorm', 'steerage', 'freight', 'dorm', 'tail',
];

export interface Emitter {
  car: number;
  x: number; y: number; z: number;
  dx: number; dy: number; dz: number;
  size: number;
  /** 0 thruster, 1 lamp (eye/marker), 2 searchlight */
  kind: number;
}
export interface Vent { car: number; x: number; y: number; z: number; rate: number; size: number }
export interface FxOut { emitters: Emitter[]; vents: Vent[] }
